import React, { useMemo } from "react";
import { ConnectionUserWithConnectionInvite, Group } from "@lib/store";
import { ConnectionInviteItem } from "./ConnectionInviteItem";
import { useTranslation } from "react-i18next";
import { ListHeader } from "../ListHeader";
import { GroupInviteItem } from "./GroupInviteItem";
import { Card } from "@atoms";
import { cn } from "@utils";

type Props = {
  pendingConnections: ConnectionUserWithConnectionInvite[];
  pendingGroupInvites: Group[];
  className?: string;
};

export const NewRequestsSection = ({ pendingConnections, pendingGroupInvites, className }: Props) => {
  const { t } = useTranslation();

  const newRequests = useMemo(() => {
    return [...pendingConnections, ...pendingGroupInvites].sort((a, b) => {
      const dateA = "connection" in a ? a.connection?.creationTime || null : a.createdAt;
      const dateB = "connection" in b ? b.connection?.creationTime : b.createdAt;

      if (dateA && dateB) {
        return dateB.localeCompare(dateA);
      } else if (dateA && !dateB) {
        return -1;
      } else if (!dateA && dateB) {
        return 1;
      }

      return 0;
    });
  }, [pendingConnections, pendingGroupInvites]);

  if (newRequests.length === 0) {
    return null;
  }

  return (
    <Card className={cn("flex flex-col px-4 sm:px-6 md:px-8 py-8 gap-6", className)}>
      <ListHeader title={t("connection:NewConnectionRequests", { count: newRequests.length })} />

      <div className="grid divide-y">
        {newRequests.map((item, index) =>
          "connection" in item ? (
            <ConnectionInviteItem
              key={item.id}
              userWithConnection={item}
            />
          ) : (
            <GroupInviteItem
              key={item.id}
              group={item}
            />
          ),
        )}
      </div>
    </Card>
  );
};
